import {
  Badge,
  Box,
  Flex,
  HStack,
  IconButton,
  Image,
  Separator,
  Text,
} from '@chakra-ui/react';
import {
  HiAdjustmentsHorizontal,
  HiBell,
  HiMagnifyingGlass,
  HiUser,
} from 'react-icons/hi2';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorModeButton } from '../ColorMode';
import { DrawerComponent } from '../DrawerComponent';
import ToggleButton from '../toggle';
import type { CurrentUser, UserMenuItem } from './Sidebar';

interface PageHeaderProps {
  icon?: React.ElementType | React.ReactElement;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  showSearch?: boolean;
  showNotifications?: boolean;
  showColorMode?: boolean;
  onSearchClick?: () => void;
  /** When provided, the bell button calls this callback instead of opening the
   *  built-in notifications drawer. Use to fully override the click behavior. */
  onNotificationClick?: () => void;
  /** When provided (and `onNotificationClick` is not), the bell button opens an
   *  internal drawer titled "Notifications" rendering this content. */
  notificationsDrawerContent?: React.ReactNode;
  notificationCount?: number;
  /** Renders a user icon button (rightmost in the action rail) when supplied
   *  alongside `userMenuItems` and/or `onUserMenuClick`. */
  currentUser?: CurrentUser;
  /** Rows for the built-in "Account" drawer (mirrors AIM's NavbarUserMenu
   *  drawer pattern). Omit when the consumer opens its own surface. */
  userMenuItems?: UserMenuItem[];
  /** When provided, this OVERRIDES the built-in "Account" drawer — same
   *  idiom as `onNotificationClick` above. Consumers whose user button should
   *  open a destination directly (a profile drawer, a route) pass this and
   *  omit `userMenuItems`, skipping the intermediate drill-down. */
  onUserMenuClick?: () => void;
  /** When true, renders an options-gear button that opens a drawer holding the
   *  theme toggle (and any caller-supplied content). The inline color-mode
   *  toggle is automatically suppressed when this is enabled. */
  showOptionsDrawer?: boolean;
  /** Body of the options drawer; replaces the default Theme/ColorMode + Screen
   *  Reader rows. Use only when you need a fully custom drawer body. */
  optionsDrawerContent?: React.ReactNode;
  /** When provided, renders a Screen Reader toggle row in the options drawer.
   *  Both the value and the change handler must be supplied. */
  screenReaderEnabled?: boolean;
  onScreenReaderToggle?: (enabled: boolean) => void;
  screenReaderTitle?: string;
  optionsDrawerTitle?: string;
  themeLabel?: string;
  notificationsDrawerTitle?: string;
}

export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  actions,
  showSearch = true,
  showNotifications = true,
  showColorMode = true,
  onSearchClick,
  onNotificationClick,
  notificationsDrawerContent,
  notificationCount = 0,
  currentUser,
  userMenuItems,
  onUserMenuClick,
  showOptionsDrawer = false,
  optionsDrawerContent,
  screenReaderEnabled,
  onScreenReaderToggle,
  screenReaderTitle,
  optionsDrawerTitle,
  themeLabel,
  notificationsDrawerTitle,
}: PageHeaderProps) {
  const { t } = useTranslation();
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  // The button needs a user plus somewhere to go — either the built-in
  // Account drawer's rows or a consumer-supplied handler.
  const hasUserMenuItems = Boolean(userMenuItems && userMenuItems.length > 0);
  const hasUserMenu = Boolean(currentUser && (hasUserMenuItems || onUserMenuClick));
  const showInlineColorMode = showColorMode && !showOptionsDrawer;
  const useInternalNotificationsDrawer =
    !onNotificationClick && Boolean(notificationsDrawerContent);
  const showScreenReaderRow =
    typeof screenReaderEnabled === 'boolean' && typeof onScreenReaderToggle === 'function';

  return (
    <Flex
      justify="space-between"
      align="center"
      gap={3}
      p={5}
      bg="surface.primary"
      borderBottomWidth="1px"
      borderColor="border.line"
    >
      {/* Left: Icon + Title/Subtitle */}
      <HStack gap={3.5} align="center" flex={1}>
        {Icon && (
          <Flex
            p={3}
            bg="surface.primary"
            borderRadius="full"
            borderWidth="1px"
            borderColor="border.secondary"
            boxShadow="sm"
            alignItems="center"
            justifyContent="center"
            color="text.action"
          >
            {React.isValidElement(Icon) ? Icon : <Icon size={28} />}
          </Flex>
        )}
        <Box>
          <Text
            fontSize="18px"
            fontWeight="600"
            color="text.primary"
            lineHeight="1.33"
            as="h1"
          >
            {title}
          </Text>
          {subtitle && (
            <Text
              fontSize="14px"
              fontWeight="400"
              color="defaultSafety.default"
              mt={1}
            >
              {subtitle}
            </Text>
          )}
        </Box>
      </HStack>

      {/* Right: Actions */}
      <HStack gap={3}>
        {showSearch && (
          <IconButton
            aria-label="Search"
            bg="button.icon"
            color="text.primary"
            borderRadius="8px"
            size="md"
            onClick={onSearchClick}
            _hover={{ bg: 'surface.tertiary' }}
            data-e2e="page-header-search-button"
          >
            <HiMagnifyingGlass size={20} />
          </IconButton>
        )}
        {showNotifications && (
          <Box position="relative" display="inline-flex">
            <IconButton
              aria-label="Notifications"
              bg="button.icon"
              color="text.primary"
              borderRadius="8px"
              size="md"
              onClick={
                onNotificationClick ??
                (useInternalNotificationsDrawer
                  ? () => setIsNotificationsOpen(true)
                  : undefined)
              }
              _hover={{ bg: 'surface.tertiary' }}
              data-e2e="page-header-notifications-button"
            >
              <HiBell size={20} />
            </IconButton>
            {notificationCount > 0 && (
              <Badge
                position="absolute"
                top="-4px"
                right="-4px"
                bg="red.500"
                color="white"
                borderRadius="full"
                fontSize="2xs"
                minW="18px"
                h="18px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px={1}
              >
                {notificationCount}
              </Badge>
            )}
          </Box>
        )}
        {showOptionsDrawer && (
          <IconButton
            aria-label="Options"
            data-e2e="optionsButton"
            bg="button.icon"
            color="text.primary"
            borderRadius="8px"
            size="md"
            onClick={() => setIsOptionsOpen(true)}
            _hover={{ bg: 'surface.tertiary' }}
          >
            <HiAdjustmentsHorizontal size={20} />
          </IconButton>
        )}
        {/* The user button is the rightmost of the built-in icons —
            after Options. `onUserMenuClick` overrides the Account drawer so a
            consumer can land on its own surface in one click. */}
        {hasUserMenu && (
          <IconButton
            aria-label="User menu"
            bg="button.icon"
            color="text.primary"
            borderRadius="8px"
            size="md"
            _hover={{ bg: 'surface.tertiary' }}
            data-e2e="currentUser"
            onClick={onUserMenuClick ?? (() => setIsUserDrawerOpen(true))}
          >
            {currentUser?.avatar ? (
              <Image
                src={currentUser.avatar}
                alt={currentUser.name}
                borderRadius="full"
                w="20px"
                h="20px"
                objectFit="cover"
              />
            ) : (
              <HiUser size={20} />
            )}
          </IconButton>
        )}
        {showInlineColorMode && <ColorModeButton />}
        {actions}
      </HStack>

      {/* User-menu drawer (matches AIM's "Account" drawer pattern). Gated on
          the rows themselves — a consumer that only passes `onUserMenuClick`
          gets the button with no Account drawer behind it. */}
      {hasUserMenu && hasUserMenuItems && (
        <DrawerComponent
          isOpen={isUserDrawerOpen}
          onClose={() => setIsUserDrawerOpen(false)}
          title="Account"
          size="sm"
          e2eTitle="navbarUserDrawer"
        >
          <Flex direction="column" gap={6}>
            {userMenuItems!.map((item, idx) => {
              const ItemIcon = item.icon;
              const isLast = idx === userMenuItems!.length - 1;
              return (
                <React.Fragment key={item.label}>
                  {/* Insert a separator above the final (Sign Out-style) row */}
                  {isLast && userMenuItems!.length > 1 && <Separator />}
                  <Flex
                    as="button"
                    // @ts-expect-error - Flex `as` doesn't carry through button props
                    type="button"
                    alignItems="center"
                    gap={2}
                    cursor="pointer"
                    bg="transparent"
                    border="none"
                    p={0}
                    textAlign="left"
                    onClick={() => {
                      setIsUserDrawerOpen(false);
                      item.onClick();
                    }}
                    color="text.primary"
                    _hover={{ color: 'text.secondary' }}
                    data-e2e={item['data-e2e']}
                  >
                    {ItemIcon && <ItemIcon size={18} color="currentColor" />}
                    <Text
                      fontSize="16px"
                      fontWeight={500}
                      color="text.primary"
                      textTransform="capitalize"
                    >
                      {item.label}
                    </Text>
                  </Flex>
                </React.Fragment>
              );
            })}
          </Flex>
        </DrawerComponent>
      )}

      {/* Options drawer */}
      {showOptionsDrawer && (
        <DrawerComponent
          isOpen={isOptionsOpen}
          onClose={() => setIsOptionsOpen(false)}
          title={optionsDrawerTitle || t('layout.options', 'Options')}
          size="sm"
          e2eTitle="optionsDrawer"
        >
          <Flex direction="column" gap={6}>
            {optionsDrawerContent ?? (
              <>
                <Flex alignItems="center" justifyContent="space-between">
                  <Text color="text.primary" fontSize="16px" fontWeight="500">
                    {themeLabel || t('layout.theme', 'Theme')}
                  </Text>
                  <ColorModeButton data-e2e="darkLightTheme" />
                </Flex>
                {showScreenReaderRow && (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <ToggleButton
                      title={screenReaderTitle || t('layout.screenReader', 'Screen Reader')}
                      e2eTitle="screenReader"
                      checked={Boolean(screenReaderEnabled)}
                      onCheckedChange={() =>
                        onScreenReaderToggle!(!screenReaderEnabled)
                      }
                    />
                  </Box>
                )}
              </>
            )}
          </Flex>
        </DrawerComponent>
      )}

      {/* Notifications drawer (only when consumer didn't pass onNotificationClick) */}
      {useInternalNotificationsDrawer && (
        <DrawerComponent
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          title={notificationsDrawerTitle || t('layout.notifications', 'Notifications')}
          size="sm"
          e2eTitle="notificationsDrawer"
        >
          {notificationsDrawerContent}
        </DrawerComponent>
      )}
    </Flex>
  );
}
